import React, { FunctionComponent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
 SelectionChangedEvent, ColGroupDef, ColDef, RowGroupingDisplayType, GetRowIdParams
} from '@ag-grid-enterprise/all-modules';
import { BackupFileDTO, BackupJobDTO } from 'data/backupRestore/backup.types';
import CommonGrid from 'shared/commonGrid';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import BackupRestoreActionMenus from '../BackupRestoreActionMenus';
import DialedDigitExtractionServerGridActionMenu from '../BackupRestoreGridActionMenu';

interface Props {
  data: BackupJobDTO[] | BackupFileDTO[],
  gridContent: 'files' | 'tasks',
  hasPrivilegeToView: boolean
  hasPrivilegesToCreate: boolean
  hasPrivilegeToDelete: boolean
  noResults: boolean,
  beforeLogout: boolean,
  lastUpdatedAt: Date | undefined,
  columnDefs: (ColGroupDef | ColDef)[],
  selectedBackupFiles?: BackupFileDTO,
  selectedJob?: BackupJobDTO,
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onSelectionChanged: (event: SelectionChangedEvent) => void
  onSelectItemMenuAction: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onShowActionMenuItem: (menuId: string, data: BackupFileDTO[] | BackupJobDTO[]) => void
  onDeleteRow?: () => void;
  onEdit: () => void;
  onRestore?: (shouldRecreateDB: boolean) => void;
  groupDefaultExpanded?: number;
  groupDisplayType?: RowGroupingDisplayType;
}

const BackupRestoreGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  hasPrivilegeToView,
  data = [],
  gridContent,
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  selectedBackupFiles,
  selectedJob,
  onDeleteRow,
  onEdit,
  onCreateNew,
  onRefresh,
  onRestore,
  onSelectionChanged,
  onShowActionMenuItem,
  onSelectItemMenuAction,
  groupDefaultExpanded,
  groupDisplayType,
}: Props) => {

  const { t } = useTranslation();

  const getRowId = useCallback((params: GetRowIdParams) => {
    if (gridContent === 'files') {
      return params?.data?.filename;
    }
    return params?.data?.id;
  }, [gridContent]);

  return (
    <div
      className="grid-container"
      style={{ height: 'inherit' }}
      data-testid="backupRestoreList"
    >
      <CommonGrid
        createNewText={
          gridContent === 'files' ? t('backupRestore.grid.newBackupFile') : ''
        }
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.SINGLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegetoView={hasPrivilegeToView}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        groupDefaultExpanded={groupDefaultExpanded}
        columnDefs={columnDefs}
        getRowId={getRowId}
        actionMenuClass={DialedDigitExtractionServerGridActionMenu}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        showActionMenuItem={onShowActionMenuItem}
        onActionMenuItemClick={onSelectItemMenuAction}
        lastUpdatedAt={lastUpdatedAt}
        groupDisplayType={groupDisplayType}
      >
        {selectedJob && (
          <BackupRestoreActionMenus
            name={selectedJob?.name || ''}
            deleteTitle={t('backupRestore.deleteJob.title')}
            restoreTitle={t('backupRestore.restorePopup.title')}
            deleteMessage={t(
              'dialedDigitExtractionServer.deletePopup.message',
              { msid: selectedJob?.name }
            )}
            restoreMessage={t(
              'dialedDigitExtractionServer.restorePopup.message',
              { msid: selectedJob?.name }
            )}
            onDelete={selectedJob?.name ? onDeleteRow : undefined}
            jobDetails={selectedJob}
          />
        )}
        {selectedBackupFiles && (
          <BackupRestoreActionMenus
            name={selectedBackupFiles?.filename || ''}
            deleteTitle={t('backupRestore.deletePopup.title')}
            restoreTitle={t('backupRestore.restorePopup.title')}
            deleteMessage={t(
              'dialedDigitExtractionServer.deletePopup.message',
              {
                msid: selectedBackupFiles?.filename
              }
            )}
            restoreMessage={t(
              'dialedDigitExtractionServer.restorePopup.message',
              { msid: selectedBackupFiles?.filename }
            )}
            onDelete={selectedBackupFiles?.filename ? onDeleteRow : undefined}
            onRestore={selectedBackupFiles?.filename ? onRestore : undefined}
            backupFile={selectedBackupFiles}
          />
        )}
      </CommonGrid>
    </div>
  );
};

export default BackupRestoreGrid;
